import React from 'react';
import { SubscriptionModal, ActivateRenewModal } from '@/components/billing';
import { SubscriptionItem } from '../SubscriptionItem';
import * as Style from './SubscriptionList.styles';
import useLocale from '@/hooks/useLocale/useLocale';
import { DiscountOfferBanner } from '../../subscriptions/DiscountOfferBanner';
import { DiscountType } from '../../subscriptions/PaymentModal/PaymentModal.types';
import { Skeleton } from 'gatorcomponents';

const SubscriptionList = ({
  activateSubscription = null,
  activeStatusLabel = 'Active',
  buttonManageSubscriptionsLabel = 'Manage',
  addonListLabel = 'Addons',
  cancelledStatusLabel = 'Cancelled',
  domainLabel = 'Domain',
  expiredStatusLabel = 'Expired',
  handleActivateSubscription,
  handleCloseRenewModal,
  loading = false,
  onMessageClick,
  modal,
  modalActiveRenewalButtonLabel = 'Confirm activation',
  modalActiveRenewalContent = 'Payment will be charged at the end of the cycle, preventing your product from being canceled and your data and files from being permanently deleted.',
  modalActiveRenewalTitle = 'Do you want to activate subscription renewal?',
  modalContent,
  onHandleManageSubscription,
  onModalClose,
  overdueStatusLabel = 'Vencida',
  pendingAnalysisStatusLabel = 'Pending Analysis',
  pendingPaymentStatusLabel = 'Pending Payment',
  pendingStatusLabel = 'Pending',
  registeredStatusLabel = 'Registred',
  subscriptions = [],
  suspendedStatusLabel = 'Suspended',
  toDueStatusLabel = 'À Vencer',
  transferredAwayStatusLabel = 'Transferred',
  openPreChatCancelFlow,
  enabledDiscountOfferBanner = false,
  orderStatusLocale,
  routesLocale,
  readMoreStepper,
  isChildrenSubscriptionList = false,
}) => {
  const { billing: billingLocale } = useLocale();
  const enabledDiscountOffer = subscription => subscription.discount && subscription.discount.type === DiscountType.RENEWAL;

  const {
    paymentMethods: paymentMethodsLocale,
  } = billingLocale;

  const handleOnModalCloseClick = () => {
    openPreChatCancelFlow && openPreChatCancelFlow();
    onModalClose && onModalClose();
  };

  return (
    <Style.Wrapper data-testid={`billing-subscriptions${isChildrenSubscriptionList ? '-addon-children' : ''}`} isChildrenSubscriptionList={isChildrenSubscriptionList}>

      {modal && (
        <SubscriptionModal
          displayModal={modal.show}
          modalContent={modalContent}
          handleActivateSubscription={handleActivateSubscription}
          onClose={handleOnModalCloseClick}
          date={modal.date}
          subscription={modal.subscription}
        />
      )}

      {activateSubscription && (
        <ActivateRenewModal
          activateSubscription={activateSubscription}
          onClose={handleCloseRenewModal}
          handleActivateSubscription={handleActivateSubscription}
          title={modalActiveRenewalTitle}
          content={modalActiveRenewalContent}
          buttonRenewalLabel={modalActiveRenewalButtonLabel}
        />
      )}

      {loading ? (
        <Style.SkeletonWrapper>
          <Skeleton variant="tableSimple" />
        </Style.SkeletonWrapper>
      ) : subscriptions.map((subscription, index) => {
        const {
          id, type, addonsList,
        } = subscription;

        const key = `subscription-item-${id}-${type ? type.toLowerCase() : ''}`;
        const testId = `billing-subscriptions-item-${type ? type.toLowerCase() : ''}-${id}`;

        const paymentMethod = paymentMethodsLocale[subscription.paymentMethod];
        const isCreditCard = paymentMethod === 'Cartão de crédito';
        const paymendMethodType = isCreditCard ? 'creditcard' : 'other';

        const hasAddonsList = addonsList && addonsList.length > 0 && addonsList.some(addon => addon.type);
        if (type === 'addon' && !isChildrenSubscriptionList) return;

        return (
          <Style.SubscriptionAdapter key={key} isChildrenSubscriptionList={isChildrenSubscriptionList}>
            <SubscriptionItem
              index={index}
              activeStatusLabel={activeStatusLabel}
              buttonManageSubscriptionsLabel={buttonManageSubscriptionsLabel}
              addonListLabel={addonListLabel}
              cancelledStatusLabel={cancelledStatusLabel}
              domainLabel={domainLabel}
              expiredStatusLabel={expiredStatusLabel}
              onMessageClick={onMessageClick}
              onSubscriptionManageClick={onHandleManageSubscription}
              overdueStatusLabel={overdueStatusLabel}
              pendingAnalysisStatusLabel={pendingAnalysisStatusLabel}
              pendingPaymentStatusLabel={pendingPaymentStatusLabel}
              pendingStatusLabel={pendingStatusLabel}
              registeredStatusLabel={registeredStatusLabel}
              subscription={subscription}
              suspendedStatusLabel={suspendedStatusLabel}
              testId={testId}
              toDueStatusLabel={toDueStatusLabel}
              transferredAwayStatusLabel={transferredAwayStatusLabel}
              orderStatusLocale={orderStatusLocale}
              routesLocale={routesLocale}
              readMoreStepper={readMoreStepper}
              paymendMethodType={paymendMethodType}
              isCreditCard={isCreditCard}
              isChildrenSubscriptionList={isChildrenSubscriptionList}
            />

            {enabledDiscountOfferBanner && enabledDiscountOffer(subscription) && (
              <DiscountOfferBanner origin="subscription_list" subscription={subscription} />
            )}

            {hasAddonsList && (
              <SubscriptionList
                activateSubscription={activateSubscription}
                activeStatusLabel={activeStatusLabel}
                buttonManageSubscriptionsLabel={buttonManageSubscriptionsLabel}
                addonListLabel={addonListLabel}
                cancelledStatusLabel={cancelledStatusLabel}
                domainLabel={domainLabel}
                expiredStatusLabel={expiredStatusLabel}
                handleActivateSubscription={handleActivateSubscription}
                handleCloseRenewModal={handleCloseRenewModal}
                loading={loading}
                onMessageClick={onMessageClick}
                modal={modal}
                modalActiveRenewalButtonLabel={modalActiveRenewalButtonLabel}
                modalActiveRenewalContent={modalActiveRenewalContent}
                modalActiveRenewalTitle={modalActiveRenewalTitle}
                modalContent={modalContent}
                onHandleManageSubscription={onHandleManageSubscription}
                onModalClose={onModalClose}
                overdueStatusLabel={overdueStatusLabel}
                pendingAnalysisStatusLabel={pendingAnalysisStatusLabel}
                pendingPaymentStatusLabel={pendingPaymentStatusLabel}
                pendingStatusLabel={pendingStatusLabel}
                registeredStatusLabel={registeredStatusLabel}
                subscriptions={addonsList}
                suspendedStatusLabel={suspendedStatusLabel}
                toDueStatusLabel={toDueStatusLabel}
                transferredAwayStatusLabel={transferredAwayStatusLabel}
                openPreChatCancelFlow={openPreChatCancelFlow}
                enabledDiscountOfferBanner={enabledDiscountOfferBanner}
                orderStatusLocale={orderStatusLocale}
                routesLocale={routesLocale}
                readMoreStepper={readMoreStepper}
                isChildrenSubscriptionList
              />
            )}
          </Style.SubscriptionAdapter>
        );
      })}
    </Style.Wrapper>
  );
};

export default SubscriptionList;
